import eq from "lodash-es/eq"

import { THEME } from "@/3514/theme"
import { buildCssClassName, getLinkDecorationTwClasses, getTransitionTwClasses } from "@/3514/utils"

import { EParticipantOnboardingStep as EStep } from "./participant.onboarding.api.config"

function getHelpHintClasses(): string {
    return "leading-[21px] text-[14px] text-gray-200 font-[300] text-pretty text-center"
}
function getHelpHintLinkClasses(): string {
    return buildCssClassName(
        "whitespace-nowrap",
        "text-blue-200",
        "hover:!text-blue-200",
        getLinkDecorationTwClasses({ color: "blue", offset: 3 })
    )
}

const _insertableStyles = {
    videoStepContent(cssIdentifier: string): string {
        return `
            .${cssIdentifier} h1, .${cssIdentifier} h2, .${cssIdentifier} h3, .${cssIdentifier} h4 { 
              color: ${THEME.COLORS.gray[100]}; 
              font-weight: ${THEME.FONT_WEIGHTS[400]};
              font-size: ${THEME.FONT_SIZES[16]}px;
              line-height: ${THEME.LINE_HEIGHTS[24]}px;
              font-family: "DM Serif Display", sans-serif;
              text-align: left;
              margin-bottom: 5px;
              @media screen and (min-width: ${THEME.BREAKPOINTS.md}) {
                font-size: ${THEME.FONT_SIZES[24]}px;
                line-height: ${THEME.LINE_HEIGHTS[36]}px;
              }
            }
            .${cssIdentifier} p, .${cssIdentifier} span { 
              color: ${THEME.COLORS.gray[100]}; 
              font-weight: ${THEME.FONT_WEIGHTS[300]};
              font-size: ${THEME.FONT_SIZES[14]}px;
              line-height: ${THEME.LINE_HEIGHTS[21]}px;
              text-align: left;
              text-wrap: pretty;
              hyphens: auto;
              @media screen and (min-width: ${THEME.BREAKPOINTS.md}) {
                font-size: ${THEME.FONT_SIZES[16]}px;
                line-height: ${THEME.LINE_HEIGHTS[24]}px;
              }
            }
        `
    }
}

export default {
    elements: {
        titleWrapper: (): string => "flex flex-col gap-y-[5px] items-centers",
        buttonContainer: (): string => "flex flex-col gap-y-[10px] items-center pb-[2px]",
        container: ({ step }: { step: EStep }): string =>
            buildCssClassName(
                "flex",
                "flex-col",
                "flex-1",
                "items-center",
                "self-center",
                "w-full",
                "gap-y-[30px]",
                "md:gap-y-[40px]",
                "py-[30px]",
                "md:py-[40px]",
                "px-[15px]",
                "md:px-0",
                [eq(step, EStep.Profile) && ["md:max-w-[500px]"]],
                [eq(step, EStep.Video) && ["md:max-w-[800px]"]],
                [eq(step, EStep.Diagnostic) && ["md:max-w-[670px]"]],
                [eq(step, EStep.Reflection) && ["md:max-w-[670px]"]],
                [eq(step, EStep.Holds) && ["md:max-w-[865px]"]]
            )
    },
    profileStep: {
        container: ({ isSmallHeightScreen }: { isSmallHeightScreen: boolean }): string =>
            buildCssClassName(
                "flex flex-col gap-y-[20px] md:gap-y-[30px] w-full px-[15px] md:px-0",
                isSmallHeightScreen && "overflow-scroll no-scrollbar max-h-[calc(100vh-200px)]"
            ),
        inputWrapper: (): string => "flex flex-col gap-y-[5px]",
        loaderWrapper: (): string =>
            "flex flex-col md:flex-row items-center justify-center gap-y-[30px] md:gap-x-[20px]",
        hintWrapper: ({ isArrowLeft }: { isArrowLeft: boolean }): string =>
            buildCssClassName(
                "relative",
                "p-[10px]",
                "bg-yellow-200",
                "rounded-[10px]",
                "w-full",
                "md:max-w-[185px]",
                "before:absolute",
                "before:w-[12px]",
                "before:h-[12px]",
                "before:bg-yellow-200",
                "before:rotate-45",
                { "before:left-[-6px] before:top-1/2 before:-translate-y-1/2": isArrowLeft },
                { "before:top-[-6px] before:left-1/2 before:-translate-x-1/2": !isArrowLeft }
            )
    },
    videoStep: {
        playerContainer: ({ isVideoAvailable }: { isVideoAvailable: boolean }): string =>
            buildCssClassName(
                "min-h-[220px] sm:min-h-[330px] h-full md:min-h-[450px] md:h-[450px] w-full",
                isVideoAvailable && "aspect-video max-md:[scale:1.015] z-10"
            ),
        editor: (): string => "w-full",
        content: _insertableStyles.videoStepContent,
        button: (): string => "mt-[20px]"
    },
    reflectionStep: {
        freeResponsesWrapper: (): string => "pt-[25px] px-[15px] md:px-[30px] pb-[40px]"
    },
    holdsStep: {
        hint: ({ isSelectionValid }: { isSelectionValid: boolean }): string =>
            buildCssClassName("pt-[3px] px-[10px] rounded-[3px]", {
                "bg-green-400": isSelectionValid,
                "bg-red-400": !isSelectionValid
            }),
        holdsContainer: (): string => "md:max-w-[590px] mb-[30px]",
        hintText: getHelpHintClasses,
        hintLink: getHelpHintLinkClasses
    },
    coachStep: {
        container: (): string => "flex flex-col gap-y-[20px] w-full",
        topContainer: (): string => "flex flex-col gap-y-[20px]",
        submittedResponses: (): string =>
            "w-full flex items-center gap-x-[15px] justify-center p-[30px] rounded-[10px] bg-blue-600",
        responsesMark: (): string => "bg-green-100 w-[20px] h-[20px] bg-green-100 rounded-full",
        coachesContainer: (): string => "flex flex-col gap-y-[25px]",
        emptyCoachesContainer: (): string =>
            "flex flex-col gap-y-[15px] py-[30px] px-[15px] md:px-0 w-full md:max-w-[600px] self-center",
        submitBtn: (): string => "mt-[40px]",
        hintText: getHelpHintClasses,
        hintLink: getHelpHintLinkClasses
    },
    stepper: {
        container: (): string => "flex w-full md:w-fit md:mx-[50px] tems-center gap-x-[2px]",
        dot: ({
            isLast,
            isCompleted: _isCompleted,
            isCurrent
        }: {
            isLast: boolean
            isCompleted: boolean
            isCurrent: boolean
        }): string =>
            buildCssClassName(
                "flex items-center justify-center",
                [
                    Boolean(isCurrent || isLast) &&
                        "min-w-[16px] w-[16px] h-[16px] border-[1px] border-blue-200 rounded-full"
                ],
                getTransitionTwClasses("all", 200)
            ),
        dotMark: ({
            isLast,
            isCompleted,
            isCurrent
        }: {
            isLast: boolean
            isCompleted: boolean
            isCurrent: boolean
        }): string =>
            buildCssClassName(
                "rounded-full",
                [
                    !isLast && [
                        ["w-[8px] h-[8px]"],
                        [Boolean(isCompleted || isCurrent) ? "bg-blue-200" : "bg-white border-[1px] border-blue-200"]
                    ]
                ],
                getTransitionTwClasses("all", 200)
            )
    }
}
